import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { defineComponent, nextTick } from 'vue'
import { trackPageView } from '@/analytics'
import { store } from '@/store'
import { setPostAuthRedirect } from '@/utils/authRedirect'
import { dashboardTabs, defaultDashboardPath, getDashboardTabFromPath } from '@/router/dashboardTabs'
import LandingView from '@/views/LandingView.vue'
import SignInView from '@/views/SignInView.vue'
import DashboardView from '@/views/DashboardView.vue'
import RegisterView from '@/views/RegisterView.vue'

const DashboardTabPlaceholder = defineComponent({
  name: 'DashboardTabPlaceholder',
  render: () => null
})

const dashboardChildRoutes: Array<RouteRecordRaw> = dashboardTabs.map((tab) => ({
  path: tab.childPath,
  name: tab.routeName,
  component: tab.value === 'workflows'
    ? () => import(/* webpackChunkName: "workflows" */ '@/views/Workflows.vue')
    : tab.value === 'pages'
      ? () => import(/* webpackChunkName: "pages" */ '@/views/PagesView.vue')
      : DashboardTabPlaceholder,
  meta: {
    title: tab.title,
    requiresAuth: true,
    category: 'application',
    feature: tab.feature
  }
}))

export const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: LandingView,
    meta: {
      title: 'MissionSquad.ai - Powerful Agentic AI Platform',
      public: true,
      category: 'marketing'
    }
  },
  {
    path: '/sign-in',
    name: 'Sign In',
    component: SignInView,
    meta: {
      title: 'Sign In - MissionSquad.ai',
      public: true,
      category: 'authentication'
    }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: {
      title: 'Register - MissionSquad.ai',
      public: true,
      category: 'authentication'
    }
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    component: () => import(/* webpackChunkName: "forgot-password" */ '@/views/ForgotPasswordView.vue'),
    meta: {
      title: 'Forgot Password - MissionSquad.ai',
      public: true,
      category: 'authentication'
    }
  },
  {
    path: '/reset-password/:token',
    name: 'Reset Password',
    component: () => import(/* webpackChunkName: "reset-password" */ '@/views/ResetPasswordView.vue'),
    meta: {
      title: 'Reset Password - MissionSquad.ai',
      public: true,
      category: 'authentication'
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: {
      title: 'Dashboard - MissionSquad.ai',
      requiresAuth: true,
      category: 'application'
    },
    children: [
      {
        path: '',
        redirect: defaultDashboardPath
      },
      ...dashboardChildRoutes
    ]
  },
  {
    // Full-screen page builder. Registered as a top-level route (not a dashboard child) so it
    // renders outside the dashboard tab window; the Pages tab still highlights because
    // getDashboardTabFromPath matches the /dashboard/pages prefix.
    path: '/dashboard/pages/:id',
    name: 'Dashboard Page Builder',
    component: () => import(/* webpackChunkName: "pages" */ '@/views/PageBuilderView.vue'),
    meta: {
      title: 'Page Builder - MissionSquad.ai',
      requiresAuth: true,
      category: 'application',
      feature: 'pages'
    }
  },
  {
    // Full-screen layout builder (visual/JSON output-schema editor), reached from the page
    // builder's "Open Layout Builder" button. Same top-level registration pattern as above.
    path: '/dashboard/pages/:id/layout',
    name: 'Dashboard Page Layout Builder',
    component: () => import(/* webpackChunkName: "pages" */ '@/views/PageLayoutBuilderView.vue'),
    meta: {
      title: 'Layout Builder - MissionSquad.ai',
      requiresAuth: true,
      category: 'application',
      feature: 'pages'
    }
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import(/* webpackChunkName: "account" */ '@/views/AccountView.vue'),
    meta: {
      title: 'Account - MissionSquad.ai',
      requiresAuth: true,
      category: 'application'
    }
  },
  {
    path: '/account/subscription',
    name: 'Manage Subscription',
    component: () => import(/* webpackChunkName: "manage-subscription" */ '@/views/ManageSubscriptionView.vue'),
    meta: {
      title: 'Manage Subscription - MissionSquad.ai',
      requiresAuth: true,
      category: 'billing'
    }
  },
  {
    path: '/subscribe',
    name: 'Subscribe',
    component: () => import(/* webpackChunkName: "subscribe" */ '@/views/SubscribeView.vue'),
    meta: {
      title: 'Subscribe - MissionSquad.ai',
      requiresAuth: true,
      category: 'billing'
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import(/* webpackChunkName: "admin" */ '@/views/AdminView.vue'),
    meta: {
      title: 'Admin - MissionSquad.ai',
      requiresAuth: true,
      requiresAdmin: true,
      category: 'application'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy Policy',
    component: () => import(/* webpackChunkName: "privacy" */ '../views/PrivacyView.vue'),
    meta: {
      title: 'Privacy Policy - MissionSquad.ai',
      public: true,
      category: 'legal'
    }
  },
  {
    path: '/terms',
    name: 'User Terms & Conditions',
    component: () => import(/* webpackChunkName: "terms" */ '../views/UserTermsView.vue'),
    meta: {
      title: 'Terms & Conditions - MissionSquad.ai',
      public: true,
      category: 'legal'
    }
  },
  {
    path: '/terms-glossary',
    name: 'Glossary of Terms',
    component: () => import(/* webpackChunkName: "glossary" */ '../views/GlossaryTermsView.vue'),
    meta: {
      title: 'Glossary - MissionSquad.ai',
      public: true,
      category: 'legal'
    }
  },
  {
    path: '/oneshot',
    name: 'OneShot',
    component: () => import(/* webpackChunkName: "oneshot" */ '../views/OneShotView.vue'),
    meta: {
      title: 'OneShot - MissionSquad.ai',
      public: true,
      category: 'tool'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import(/* webpackChunkName: "contact" */ '@/views/ContactView.vue'),
    meta: {
      title: 'Contact Us - MissionSquad.ai',
      public: true,
      category: 'marketing'
    }
  },
  {
    path: '/agent/:username/:agentslug',
    name: 'Public Agent',
    component: () => import(/* webpackChunkName: "public-agent" */ '@/views/PublicAgentView.vue'),
    meta: {
      title: 'Public Agent - MissionSquad.ai',
      public: true,
      category: 'public_agent'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// Auth/Admin guard
router.beforeEach((to, from, next) => {
  const requiresAuth = (to.meta as any)?.requiresAuth
  const requiresAdmin = (to.meta as any)?.requiresAdmin
  const isAuthenticated = store.getters.authenticated
  const isAdmin = store.getters.isAdmin

  if (requiresAuth && !isAuthenticated) {
    const selectedTab = getDashboardTabFromPath(to.path)
    setPostAuthRedirect({
      fullPath: to.fullPath,
      path: to.path,
      selectedTab,
      source: 'router'
    })
    return next({ path: '/sign-in' })
  }
  if (requiresAdmin && !isAdmin) {
    return next({ path: defaultDashboardPath })
  }
  const dashboardTab = getDashboardTabFromPath(to.path)
  if (dashboardTab != null && store.getters.selectedTab !== dashboardTab) {
    store.dispatch('setTab', dashboardTab)
  }
  return next()
})

// Enhanced route tracking
router.afterEach((to, from) => {
  nextTick(() => {
    // Update document title
    if (to.meta?.title) {
      document.title = to.meta.title as string
    }

    void trackPageView({
      hostname: window.location.hostname,
      origin: window.location.origin,
      page_path: to.fullPath,
      page_title: document.title,
      page_location: window.location.href,
      route_name: to.name != null ? String(to.name) : undefined,
      route_category: typeof to.meta?.category === 'string' ? to.meta.category : undefined,
      route_feature: typeof to.meta?.feature === 'string' ? to.meta.feature : undefined,
      authenticated: Boolean(store.getters.authenticated),
      selected_tab: getDashboardTabFromPath(to.path) ?? (typeof store.getters.selectedTab === 'string' ? store.getters.selectedTab : undefined),
      public_agent_username: typeof to.params.username === 'string' ? to.params.username : undefined,
      public_agent_slug: typeof to.params.agentslug === 'string' ? to.params.agentslug : undefined
    })
  })
})

export default router
